import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckIcon, XIcon, ClockIcon, SparklesIcon } from 'lucide-react';
import ApplyButton from './ApplyButton';
interface DeliverableProps {
  number: number;
  title: string;
  whatItIs: string;
  delay: number;
  isLast?: boolean;
}
const Deliverable = ({
  number,
  title,
  whatItIs,
  delay,
  isLast
}: DeliverableProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay
  }} className="relative pl-10 sm:pl-14" data-id="element-274">
      {/* Timeline dot */}
      <div className="absolute left-0 top-6 sm:top-8 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#ffdf07] border-4 border-black z-10 shadow-[0_0_10px_rgba(255,223,7,0.5)]" data-id="element-275" />

      {/* Card */}
      <div className={`bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-5 sm:p-8 ${!isLast ? 'mb-5 sm:mb-8' : ''} shadow-sm`} data-id="element-276">
        <div className="flex items-start sm:items-center mb-4 sm:mb-5" data-id="element-277">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#ffdf07] flex items-center justify-center text-black font-bold mr-4 sm:mr-5 flex-shrink-0 text-lg sm:text-xl" data-id="element-278">
            {number}
          </div>
          <h3 className="text-lg sm:text-2xl md:text-3xl text-white typekit-heading leading-tight" data-id="element-279">
            {title}
          </h3>
        </div>
        <div className="ml-0 sm:ml-16" data-id="element-280">
          <p className="text-gray-300 libre-caslon-text-regular leading-relaxed text-base sm:text-lg" data-id="element-281">
            {whatItIs}
          </p>
        </div>
      </div>
    </motion.div>;
};
interface TimelineSectionProps {
  scrollToCalendar?: () => void;
}
const TimelineSection = ({
  scrollToCalendar
}: TimelineSectionProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [processRef, processInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [comparisonRef, comparisonInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [unlockRef, unlockInView] = useInView({
    triggerOnce: true,
    threshold: 0.3
  });
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: timelineContainerRef,
    offset: ['start center', 'end center']
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const deliverables = [{
    number: 1,
    title: 'Revenue Cycle Cash Map',
    whatItIs: "Find out exactly where money is slipping through the cracks – unpaid claims, slow collections, denials you didn't know about."
  }, {
    number: 2,
    title: 'Clinic Cash & Capital Score',
    whatItIs: "Get a clear picture of your clinic's financial health – how fast you're collecting, your runway, and what your practice is actually worth."
  }, {
    number: 3,
    title: 'Capital Reality Check',
    whatItIs: 'Know where you stand before you need a loan or talk to a buyer. No surprises when it matters most.'
  }, {
    number: 4,
    title: '90-Day Action Map',
    whatItIs: 'Walk away with a clear plan: what to fix first, who handles what, and what you can tackle yourself.'
  }, {
    number: 5,
    title: 'Growth Milestones',
    whatItIs: 'See the exact numbers you need to hit before you can step back, hire, or expand. Real targets based on your financials.'
  }];
  const unlockItems = ['Hire providers confidently', 'Open new locations', 'Pursue acquisitions', 'Sell on your terms', 'Step back from patient care', 'Less stress, more clarity'];
  const comparisonData = [{
    feature: 'Revenue Cycle Analysis',
    traditional: '$15K – $40K',
    sprint: '✓ Included'
  }, {
    feature: 'Financial Health Assessment',
    traditional: '$10K – $25K',
    sprint: '✓ Included'
  }, {
    feature: 'Capital Readiness Review',
    traditional: '$20K – $50K',
    sprint: '✓ Included'
  }, {
    feature: 'Strategic Action Plan',
    traditional: '$15K – $30K',
    sprint: '✓ Included'
  }, {
    feature: 'Growth Milestone Modeling',
    traditional: '$10K – $20K',
    sprint: '✓ Included'
  }];
  return <section className="w-full bg-black py-12 sm:py-20 px-3 sm:px-4" data-id="element-282">
      <div className="max-w-4xl mx-auto" data-id="element-283">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-10 sm:mb-14" data-id="element-284">
          <h2 className="text-white text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6 typekit-heading" data-id="element-285">
            What You Get In 30 Days
          </h2>
        </motion.div>

        {/* Timeline with connected line */}
        <div ref={timelineContainerRef} className="relative mb-12 sm:mb-20" data-id="element-286">
          {/* Background line (gray) */}
          <div className="absolute left-[9px] sm:left-[11px] top-8 bottom-8 w-[3px] bg-gray-800" data-id="element-287" />

          {/* Animated illuminated line (yellow) */}
          <motion.div className="absolute left-[9px] sm:left-[11px] top-8 w-[3px] bg-gradient-to-b from-[#ffdf07] via-[#ffdf07] to-[#ffdf07] shadow-[0_0_10px_rgba(255,223,7,0.6)]" style={{
          height: lineHeight
        }} data-id="element-288" />

          {deliverables.map((item, index) => <Deliverable key={index} number={item.number} title={item.title} whatItIs={item.whatItIs} delay={0.1 * index} isLast={index === deliverables.length - 1} data-id="element-289" />)}

          {/* 6th Card - What This Unlocks */}
          <motion.div ref={unlockRef} initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} animate={unlockInView ? {
          opacity: 1,
          y: 0,
          scale: 1
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative pl-10 sm:pl-14 mt-5 sm:mt-8" data-id="element-290">
            {/* Timeline dot - special golden */}
            <div className="absolute left-0 top-6 sm:top-8 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#ffdf07] border-4 border-black z-10 shadow-[0_0_15px_rgba(255,223,7,0.8)]" data-id="element-291" />

            {/* Special unlock card */}
            <div className="bg-gradient-to-br from-[#ffdf07]/20 via-[#1a1a0a] to-[#0a0a0a] border border-[#ffdf07]/40 rounded-lg p-5 sm:p-8 shadow-[0_0_20px_rgba(255,223,7,0.15)]" data-id="element-292">
              <div className="flex items-center mb-4 sm:mb-6" data-id="element-293">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#ffdf07] flex items-center justify-center mr-4 sm:mr-5 flex-shrink-0" data-id="element-294">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black" data-id="element-295" />
                </div>
                <h3 className="text-lg sm:text-2xl md:text-3xl text-[#ffdf07] typekit-heading leading-tight" data-id="element-296">
                  What This Unlocks
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-297">
                {unlockItems.map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={unlockInView ? {
                opacity: 1,
                y: 0
              } : {}} transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.1
              }} className="flex items-center" data-id="element-298">
                    <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#ffdf07] mr-2 sm:mr-3 flex-shrink-0" data-id="element-299" />
                    <span className="text-gray-200 libre-caslon-text-regular text-sm sm:text-base" data-id="element-300">
                      {item}
                    </span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Comparison Section */}
        <motion.div ref={comparisonRef} initial={{
        opacity: 0,
        y: 20
      }} animate={comparisonInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mt-12 sm:mt-20 pt-10 sm:pt-16 border-t border-gray-800" data-id="element-301">
          <h3 className="text-white text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6 typekit-heading text-center" data-id="element-302">
            <span className="block" data-id="element-303">CFO-Level Clarity</span>
            <span className="block" data-id="element-304">Without the CFO Price Tag</span>
          </h3>
          <p className="text-gray-400 libre-caslon-text-regular text-center mb-8 sm:mb-12 text-base sm:text-xl px-1 max-w-2xl mx-auto" data-id="element-305">
            A CFO costs <span className="text-white" data-id="element-306">$150K–$300K/year</span>.
            Big firms charge <span className="text-white" data-id="element-307">$50K–$150K</span> per
            project. The Sprint gives you the same clarity for a fraction.
          </p>

          {/* T-Chart Comparison - Sprint on LEFT */}
          <div className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg overflow-hidden" data-id="element-308">
            {/* Header */}
            <div className="grid grid-cols-2 border-b border-gray-800" data-id="element-309">
              <div className="p-5 sm:p-8 border-r border-gray-800 bg-[#ffdf07]/10" data-id="element-310">
                <div className="flex items-center justify-center gap-2 sm:gap-3" data-id="element-311">
                  <CheckIcon className="text-[#ffdf07] w-6 h-6 sm:w-8 sm:h-8" data-id="element-312" />
                  <h4 className="text-[#ffdf07] text-base sm:text-xl typekit-heading text-center" data-id="element-313">
                    The Sprint
                  </h4>
                </div>
                <p className="text-gray-400 text-sm sm:text-base text-center mt-2 libre-caslon-text-regular" data-id="element-314">
                  30 Days, All-Inclusive
                </p>
              </div>
              <div className="p-5 sm:p-8 bg-gray-900/50" data-id="element-315">
                <div className="flex items-center justify-center gap-2 sm:gap-3" data-id="element-316">
                  <XIcon className="text-red-500 w-6 h-6 sm:w-8 sm:h-8" data-id="element-317" />
                  <h4 className="text-white text-base sm:text-xl typekit-heading text-center" data-id="element-318">
                    Traditional Route
                  </h4>
                </div>
                <p className="text-gray-500 text-sm sm:text-base text-center mt-2 libre-caslon-text-regular" data-id="element-319">
                  Big Firms / Fractional CFO
                </p>
              </div>
            </div>

            {/* Comparison Rows */}
            {comparisonData.map((row, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={comparisonInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 * index
          }} className={`grid grid-cols-2 ${index !== comparisonData.length - 1 ? 'border-b border-gray-800' : ''}`} data-id="element-320">
                <div className="p-4 sm:p-6 border-r border-gray-800 bg-[#ffdf07]/5" data-id="element-321">
                  <p className="text-gray-400 text-sm sm:text-base libre-caslon-text-regular mb-1" data-id="element-322">
                    {row.feature}
                  </p>
                  <p className="text-[#ffdf07] text-base sm:text-lg font-semibold" data-id="element-323">
                    {row.sprint}
                  </p>
                </div>
                <div className="p-4 sm:p-6" data-id="element-324">
                  <p className="text-gray-400 text-sm sm:text-base libre-caslon-text-regular mb-1" data-id="element-325">
                    {row.feature}
                  </p>
                  <p className="text-red-400 text-base sm:text-lg font-semibold" data-id="element-326">
                    {row.traditional}
                  </p>
                </div>
              </motion.div>)}

            {/* Total Row */}
            <div className="grid grid-cols-2 border-t-2 border-gray-700" data-id="element-327">
              <div className="p-5 sm:p-8 border-r border-gray-800 bg-[#ffdf07]/10" data-id="element-328">
                <p className="text-gray-400 text-sm sm:text-base libre-caslon-text-regular mb-1" data-id="element-329">
                  Your Investment
                </p>
                <p className="text-[#ffdf07] text-xl sm:text-3xl typekit-heading" data-id="element-330">
                  A Fraction of the Cost
                </p>
                <p className="text-gray-400 text-sm sm:text-base mt-2 libre-caslon-text-regular" data-id="element-331">
                  Delivered in 30 days
                </p>
              </div>
              <div className="p-5 sm:p-8 bg-gray-900/30" data-id="element-332">
                <p className="text-gray-500 text-sm sm:text-base libre-caslon-text-regular mb-1" data-id="element-333">
                  Total Investment
                </p>
                <p className="text-red-400 text-xl sm:text-3xl typekit-heading" data-id="element-334">
                  $70K – $165K+
                </p>
                <p className="text-gray-600 text-sm sm:text-base mt-2 libre-caslon-text-regular" data-id="element-335">
                  Plus 3–6 months timeline
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-500 libre-caslon-text-regular text-center mt-6 sm:mt-8 text-sm sm:text-base px-1" data-id="element-336">
            * Traditional pricing based on industry averages
          </p>

          {/* CTA Button after comparison */}
          <div className="mt-10 sm:mt-12" data-id="element-337">
            {scrollToCalendar ? <div className="flex justify-center" data-id="element-338">
                <div className="relative" data-id="element-339">
                  <div className="button-border-light" data-id="element-340"></div>
                  <motion.button onClick={scrollToCalendar} whileHover={{
                scale: 1.05,
                boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
              }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 sm:py-4 px-8 sm:px-12 rounded-md text-base sm:text-lg shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-341">
                    Book My Strategy Call
                  </motion.button>
                </div>
              </div> : <ApplyButton data-id="element-342" />}
          </div>
        </motion.div>

        {/* How It Works Section */}
        <motion.div ref={processRef} initial={{
        opacity: 0,
        y: 20
      }} animate={processInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mt-12 sm:mt-20 pt-10 sm:pt-16 border-t border-gray-800" data-id="element-343">
          <h3 className="text-white text-2xl sm:text-3xl md:text-4xl mb-8 sm:mb-10 typekit-heading text-center" data-id="element-344">
            How It Works
          </h3>

          {/* 6 Hours Visual Highlight */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={processInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex justify-center mb-10 sm:mb-12" data-id="element-345">
            <div className="bg-gradient-to-r from-[#ffdf07]/20 via-[#ffdf07]/10 to-[#ffdf07]/20 border border-[#ffdf07]/40 rounded-xl px-8 sm:px-12 py-6 sm:py-8 text-center" data-id="element-346">
              <div className="flex items-center justify-center gap-4 mb-3" data-id="element-347">
                <ClockIcon className="text-[#ffdf07] w-10 h-10 sm:w-14 sm:h-14" data-id="element-348" />
                <span className="text-[#ffdf07] text-4xl sm:text-6xl typekit-heading" data-id="element-349">
                  ~6 Hours
                </span>
              </div>
              <p className="text-gray-300 libre-caslon-text-regular text-base sm:text-xl" data-id="element-350">
                Total time from your team over 30 days
              </p>
            </div>
          </motion.div>

          <div className="space-y-5 sm:space-y-8" data-id="element-351">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={processInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-5 sm:p-8 shadow-sm" data-id="element-352">
              <h4 className="text-white text-xl sm:text-2xl mb-3 sm:mb-4 typekit-heading" data-id="element-353">
                Week 1 – Intake
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular text-base sm:text-lg" data-id="element-354">
                Share read-only access to your systems. Two 60-90 min interviews
                (founder + billing lead).
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={processInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-5 sm:p-8 shadow-sm" data-id="element-355">
              <h4 className="text-white text-xl sm:text-2xl mb-3 sm:mb-4 typekit-heading" data-id="element-356">
                Week 2 – Analysis
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular text-base sm:text-lg" data-id="element-357">
                We build your Cash Map, Score, Capital Reality Check, and Growth
                Milestones.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={processInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-5 sm:p-8 shadow-sm" data-id="element-358">
              <h4 className="text-white text-xl sm:text-2xl mb-3 sm:mb-4 typekit-heading" data-id="element-359">
                Week 3 – Readout
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular text-base sm:text-lg" data-id="element-360">
                90-min working session walking through findings, opportunities,
                and your 90-Day Action Map.
              </p>
            </motion.div>
          </div>
        </motion.div>

        <div className="mt-10 sm:mt-14" data-id="element-361">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-362">
              <div className="relative" data-id="element-363">
                <div className="button-border-light" data-id="element-364"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 sm:py-4 px-8 sm:px-12 rounded-md text-base sm:text-lg shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-365">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-366" />}
        </div>
      </div>
    </section>;
};
export default TimelineSection;